import React, { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  ERROR_TYPE,
  FEATURES,
  PLAYLIST_ERROR_ACTIONS,
  PLAYLIST_INFO_ACTIONS,
} from '@soundxyz/utils/constants/logging';
import {
  CoverImages,
  getPlaylistBackgroundDominantColor,
} from '@components/Playlists/basics/CoverImages';
import { PreviewDropdown } from '@components/Playlists/basics/Dropdown';
import { MintsDrivenTrigger } from '@components/Playlists/basics/MintsDrivenTrigger';
import { usePlaylist } from '@components/Playlists/usePlaylist';
import { SLogoSoundBarOverlay } from '@components/SoundBar/SLogoSoundBarOverlay';
import { PlayIndicatorBars } from '@components/main-components/cards/PlayIndicatorBars';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { PlayButton } from '@components/main-components/play-button/PlayButton';
import { useAuth } from '@context/auth/base';
import { useMediaQuery } from '@context/mediaQuery';
import type { FragmentType } from '@graphql/reactQuery';
import {
  getFragment,
  PlaylistPreviewFragmentDoc,
  PreviewHeaderFragmentDoc,
  ShelfCreatorFragmentDoc,
} from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogErrorNew';
import { usePlaylistPlayingState } from '@hooks/usePlaylistPlayingState';
import { BULLET } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

export const MAX_MINIMIZED_RELEASES = 4;

export const PlaylistWidget = ({
  playlistFrag,
}: {
  playlistFrag: FragmentType<PlaylistPreviewFragmentDoc>;
}) => {
  const logError = useLogError();
  const playlist = getFragment(PlaylistPreviewFragmentDoc, playlistFrag);

  const playlistHeaderInfo = getFragment(PreviewHeaderFragmentDoc, playlist);

  const { id, releaseCount, user, webappUri } = playlist;
  const { name, type, numReferralPurchases: mintsDriven } = playlistHeaderInfo;
  const { tablet } = useMediaQuery();

  const { username: playlistCreatorUsername, webappUri: playlistCreatorWebappUri } = getFragment(
    ShelfCreatorFragmentDoc,
    playlistHeaderInfo.user,
  );

  const { authUser } = useAuth();
  const isOwner = authUser?.id === user.id;

  const { trackIDs, error } = usePlaylist({
    playlistId: id,
    releaseCount,
    limit: MAX_MINIMIZED_RELEASES,
    dateSort: 'DESC',
    type: 'previewPlaylistRow',
  });

  const { isPlaying, onPlayButtonClick } = usePlaylistPlayingState({
    playlistId: id,
  });

  useEffect(() => {
    if (!error) return;
    logError({
      error,
      feature: FEATURES.PLAYLISTS,
      isArtistOnlyPage: false,
      message: 'Error loading data for playlist preview',
      action: PLAYLIST_ERROR_ACTIONS.PLAYLIST_PREVIEW_FETCH_ERROR,
      errorType: ERROR_TYPE.FETCH_GQL_ERROR,
      level: 'error',
      indexedTags: {
        playlistId: id,
      },
    });
  }, [error, id, logError]);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="align-center relative flex w-full items-center overflow-hidden rounded-lg p-4"
        style={{ background: getPlaylistBackgroundDominantColor(playlistHeaderInfo) ?? undefined }}
      >
        <div className="absolute inset-0 left-0 top-0 w-full overflow-hidden bg-neutral900 opacity-40" />
        <div className="absolute left-[300px] top-0 overflow-hidden">
          <SLogoSoundBarOverlay className="relative overflow-hidden" />
        </div>

        <div className="color-white absolute right-4 top-4 z-above1 flex">
          <PreviewDropdown
            isDropdownOpen={isDropdownOpen}
            setIsDropdownOpen={setIsDropdownOpen}
            playlist={playlistHeaderInfo}
            trackIds={trackIDs}
            playlistLink={webappUri}
            isOwner={isOwner}
            playlistType={type}
            variant="inverted"
            triggerProps={{ size: 24 }}
            source="Feed Playlist Post"
          />
        </div>

        <div className="relative flex flex-1 items-center gap-4 overflow-hidden md:items-stretch">
          <div className="relative aspect-square w-16 flex-00auto overflow-hidden rounded-md transition-opacity duration-300 ease-in-out md:rounded-lg">
            <CoverImages playlist={playlistHeaderInfo} maxSize={64} priority={false} />
            <div
              className={twMerge(
                'absolute inset-0',
                'items-center justify-center transition-opacity duration-300 ease-in-out md:opacity-0 md:hover:opacity-100',
                'z-above1 flex',
              )}
            >
              <PlayButton
                color="white"
                size="s"
                onClick={onPlayButtonClick}
                playing={isPlaying}
                className="playlistPlayButton group-hover/preview:visible"
                elevated
                currentListeningParty={null}
              />
            </div>
          </div>
          <div className="flex flex-1 flex-col justify-between gap-1 overflow-hidden md:pt-1">
            <div className="overflow-hidden text-ellipsis whitespace-nowrap font-base text-[13px] leading-[18px] text-white">
              <AccessibleLink pathnameUri={webappUriPath(playlistCreatorWebappUri)}>
                {playlistCreatorUsername}
              </AccessibleLink>
            </div>
            <AccessibleLink
              className="flex items-center gap-2"
              pathnameUri={webappUriPath(webappUri)}
            >
              <div className="flex w-full items-center gap-2 whitespace-nowrap font-title text-title-s font-medium text-white">
                {isPlaying && <PlayIndicatorBars smallBars={!tablet} />}
                <AccessibleLink
                  className="overflow-hidden text-ellipsis"
                  pathnameUri={webappUriPath(webappUri)}
                >
                  {name}
                </AccessibleLink>
              </div>
            </AccessibleLink>
            <div className="flex text-base-xs leading-[18px] text-white opacity-60">
              {releaseCount > 0 ? (
                <div className="mb-1 inline-flex truncate md:mb-0">
                  <div className="truncate">
                    {releaseCount} {getSingularOrPlural({ count: releaseCount, word: 'sound' })}
                  </div>
                  {mintsDriven ? (
                    <>
                      {BULLET}
                      <MintsDrivenTrigger
                        className="truncate transition-[filter] duration-300 hover:brightness-130"
                        playlistId={id}
                        mintsDriven={mintsDriven}
                        infoAction={PLAYLIST_INFO_ACTIONS.PLAYLIST_PREVIEW_MINTS_DRIVEN_CLICK}
                      />
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
